/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {Router} from "@angular/router";
import {FabConfirmationDialogComponent} from "@fabric/components/confirmation-dialog";
import {FabMessageService} from "@fabric/components/message";
import {DISMISS_MSG, GMTVP} from "../../common/constants";
import {
  IndustryScenarioSelectPopUpComponent
} from '../../core/components/industry-scenario-select/industry-scenario-select-pop-up/industry-scenario-select-pop-up.component';
import {
  VehicleScenarioSelectPopUpComponent
} from "../../core/components/vehicle-scenario-select/vehicle-scenario-select-pop-up/vehicle-scenario-select-pop-up.component";
import {StateService} from "../../core/state/state.service";
import {IndustryScenario} from "../../models/industry-scenario-dto.model";
import {VehicleScenario} from "../../models/vehicle-scenario-dto.model";
import {IndustryScenarioService} from "../../services/industry-scenario.service";
import {PropulsionScenarioService} from "../../services/propulsion-scenario-service";
import {VehicleScenarioService} from "../../services/vehicle-scenario.service";
import {QueryIndustryPopUpComponent} from "../industry-scenario/query-industry-pop-up/query-industry-pop-up.component";
import {
  AttachPropulsionScenarioPopUpComponent
} from "../vehicle-scenario/attach-propulsion-scenario-pop-up/attach-propulsion-scenario-pop-up.component";
import {
  SelectVehicleProgramPopUpComponent
} from "../vehicle-scenario/create-vehicle-scenario/select-vehicle-program-pop-up/select-vehicle-program-pop-up.component";
import {
  UpdatePropulsionScenarioWithPortComponent
} from "../vehicle-scenario/update-propulsion-scenario-with-port/update-propulsion-scenario-with-port.component";

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit {

  protected readonly GMTVP = GMTVP;
  showSpinner: boolean;

  // Vehicle Scenario variables
  selectedVehicleScenario: VehicleScenario;
  compareVehicleScenario: VehicleScenario;

  selectedIndustryScenario: IndustryScenario;

  constructor(public snackBar: FabMessageService,
              readonly vehicleScenarioService: VehicleScenarioService,
              readonly industryScenarioService: IndustryScenarioService,
              public stateService: StateService,
              public dialog: MatDialog,
              readonly router: Router,
              readonly propulsionScenarioService: PropulsionScenarioService) {
  }

  ngOnInit(): void {
    if (this.stateService.selectedScenario && this.stateService.isVehicleScenarioSelected) {
      this.selectedVehicleScenario = this.stateService.selectedScenario;
      this.selectedIndustryScenario = this.stateService.selectedScenario.industryScenario;
      this.compareVehicleScenario = this.stateService.selectedCompareScenario ? this.stateService.selectedCompareScenario : this.stateService.selectedScenario;
    } else if (this.stateService.selectedScenario && !this.stateService.isVehicleScenarioSelected) {
      this.selectedIndustryScenario = this.stateService.selectedScenario;
    }
  }

  /**
   * Opens the pop-up to select a vehicle scenario
   */
  openVehSelectComponent = (): void => {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        isMultiselect: false,
        title: 'Select Vehicle Scenario',
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.selectedVehicleScenario = res.selectedScenarios[0];
        this.selectedIndustryScenario = this.selectedVehicleScenario.industryScenario;

        if (this.compareVehicleScenario?.fcorg !== this.selectedVehicleScenario.fcorg) {
          this.compareVehicleScenario = this.selectedVehicleScenario;
        }
        this.stateService.setSelectedScenario(this.selectedVehicleScenario, true);
        this.stateService.setCompareScenario(this.compareVehicleScenario);
      }
    });
  }


  /**
   * Opens the SelectCompareScenario pop up component
   */
  selectCompareScenario(): void {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        validFcorgs: [this.selectedVehicleScenario.fcorg],
        title: 'Select Compare Scenario',
        isMultiselect: false,
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.compareVehicleScenario = res.selectedScenarios[0];
        this.stateService.setCompareScenario(this.compareVehicleScenario);
      }
    });
  }

  /**
   * Open the Attach Propulsion Scenario dialog
   */
  openAttachPropScenDialog(): void {
    const dialogRef = this.dialog.open(AttachPropulsionScenarioPopUpComponent, {
      height: '80%',
      width: '80%',
      data: {
        selectedVehicleScenario: this.selectedVehicleScenario,
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.selectedVehicleScenario.propulsionScenario = res;
      }
    });
  }

  /**
   * Opens the pop-up to select a vehicle scenario
   */
  openIndSelectComponent(): void {
    const dialogRef = this.dialog.open(IndustryScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        isMultiSelect: false,
        title: 'Select Industry Scenario',
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.selectedIndustryScenario = res.selectedScenarios[0];
        this.selectedVehicleScenario = null;
        this.compareVehicleScenario = null;
        this.stateService.setSelectedScenario(this.selectedIndustryScenario, false);
        this.stateService.setCompareScenario(null);
      }
    });
  }

  /**
   * Opens the Query Industry pop up
   */
  openQueryIndustryPopUp(): void {
    this.dialog.open(QueryIndustryPopUpComponent, {
      height: '90%',
      data: {
        scenario: this.selectedIndustryScenario
      }
    });
  }

  /**
   * Navigate to the Query Vehicle/Create Vehicle Forecast Template page
   */
  navToQueryVehicle(): void {
    this.router.navigate(['/vehicle/forecast-template/create']);
  }

  /**
   * Navigate to the Query Vehicle/Create Vehicle Forecast Template page
   */
  navToQueryVehicleSave(): void {
    this.router.navigate(['/vehicle/forecast-template/save']);
  }

  /**
   * Navigates to create industry scenario page
   */
  navToQueryIndustrySave(): void {
    this.router.navigate(['/industry/forecast-template/save']);
  }

  /**
   * Opens the page to create the propulsion forecast template
   */
  navToCreatePropulsionForecastTemplate(): void {
    this.router.navigate(['/propulsion/forecast-template/create'])
  }

  /**
   * Opens the page to save a propulsion forecast template
   */
  navToSavePropulsionForecastTemplate(): void {
    this.router.navigate(['/propulsion/forecast-template/save'])
  }

  /**
   * Opens the page to update a vehicle scenario with another scenario
   */
  navToUpdateForecastWithScenario(): void {
    this.router.navigate(['/vehicle/update-with-scen'])
  }

  /**
   * Opens the page to update a vehicle scenario with another scenario
   */
  navToUpdatePropForecastWithScenario(): void {
    this.router.navigate(['/propulsion/update-with-scen'])
  }

  /**
   * Opens the page to update a vehicle scenario with external data
   */
  navToUpdateVehWithExtData(): void {
    this.router.navigate(['/vehicle/update-with-ext-data'])
  }

  /**
   * Opens the page to update a propulsion scenario with external data
   */
  navToUpdatePropWithExtData(): void {
    this.router.navigate(['/propulsion/update-with-ext-data'])
  }

  /**
   * Navigate to the delete propulsion scenario detail page
   */
  navToDeletePropDetails(): void {
    this.router.navigate(['/propulsion/delete-prop-details'])
  }

  /**
   * Navigate to the delete vehicle scenario detail page
   */
  navToDeleteVehDetails(): void {
    this.router.navigate(['/vehicle/delete-veh-details'])
  }

  /**
   * Event handler for when the Update With Vehicle Portfolio menu item is clicked
   */
  onUpdateWithVehPortfolioClick(): void {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Update Vehicle Scenario with Vehicle Portfolio',
        message: 'Do you want to update the scenario with the vehicle portfolio?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    });

    // If user confirms they want to proceed with the update
    dialogRef.componentInstance.yes.subscribe(() => {
      if (this.selectedVehicleScenario.fcorg === GMTVP) {
        // If the selected scenario is a GMTVP scenario
        // Get the ARCH value for the GMTVP scenario
        let architecture = '';
        this.showSpinner = true;
        this.vehicleScenarioService.getArchByVehScenId(this.selectedVehicleScenario.vehScenId).subscribe({
          next: data => {
            architecture = data[0];
          },
          error: err => {
            this.showSpinner = false;
            this.snackBar.displayError(err, DISMISS_MSG);
          },
          complete: () => {
            this.showSpinner = false;
            this.openSelectVehicleProgramsPopUp(architecture);
          }
        });
      } else {
        this.updateWithVehiclePortfolio(this.selectedVehicleScenario.vehScenId,
          this.stateService.getUserGmid(), '', '', '', '', '', '');
      }
    });
  }

  /**
   * Make service call to update a vehicle scenario with the vehicle portfolio
   * @param vehScenId the vehicle scenario id of the scenario to update
   * @param gmid the id of the user
   * @param arch the architecture value (only applicable to TVP scenarios)
   * @param mktDiv concatenated string of market divisions (only applicable to TVP scenarios)
   * @param brand concatenated string of brands (only applicable to TVP scenarios)
   * @param prgmCode concatenated string of program codes (only applicable to TVP scenarios)
   * @param styleCode concatenated string of style codes (only applicable to TVP scenarios)
   * @param bodyStyle concatenated string of body style (only applicable to TVP scenarios)
   */
  updateWithVehiclePortfolio(vehScenId: number, gmid: string, arch: string, mktDiv: string,
                             brand: string, prgmCode: string, styleCode: string, bodyStyle: string): void {
    this.showSpinner = true;
    this.vehicleScenarioService
    .updateVehicleScenarioWithPortfolio(vehScenId, gmid, arch, mktDiv, brand, prgmCode, styleCode, bodyStyle)
    .subscribe({
      error: err => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('The vehicle scenario was successfully updated with the portfolio', DISMISS_MSG);
      }
    });
  }

  /**
   * Open the Select Vehicle Programs pop up
   * @param architecture the architecture to select vehicle programs from
   */
  openSelectVehicleProgramsPopUp(architecture: string): void {
    this.dialog.open(SelectVehicleProgramPopUpComponent, {
      width: '50%',
      data: {
        architecture,
        vehScenId: this.selectedVehicleScenario.vehScenId
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        // Generate parameters to pass to the updateWithVehiclePortfolio method
        const programs = result.data.programs;
        const mktDiv = programs.map(program => program.mktDiv).join(',');
        const brand = programs.map(program => program.brand).join(',');
        const prgmCode = programs.map(program => program.prgmCode).join(',');
        const styleCode = programs.map(program => program.styleCode).join(',');
        const bodyStyle = programs.map(program => program.bodyStyle).join(',');

        this.updateWithVehiclePortfolio(this.selectedVehicleScenario.vehScenId,
          this.stateService.getUserGmid(), architecture, mktDiv, brand, prgmCode, styleCode, bodyStyle);
      }
    });
  }

  /**
   * Event handler for when the Update With Propulsion Portfolio menu item is clicked
   */
  onUpdateWithPropPortfolioClick(): void {
    this.showSpinner = true;
    let dupeRecords = undefined;
    // Check if the propulsion scenario has any duplicates
    this.propulsionScenarioService.getPropDupeRecords(this.stateService.selectedScenario.propulsionScenario.id).subscribe({
      next: data => {
        dupeRecords = data;
      },
      error: err => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        if (dupeRecords && dupeRecords.length > 0) {
          this.snackBar.displayError('The selected propulsion scenario has duplicate records. Please resolve the duplicates before proceeding.', DISMISS_MSG);
        } else {
          this.dialog.open(UpdatePropulsionScenarioWithPortComponent, {
            height: '60%',
            width: '40%',
          });
        }
      }
    });

  }


}
